import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { eur } from "@/lib/api";
import { toast } from "sonner";
import { Crosshair, ShieldCheck, Spinner, TrendUp, ArrowRight, Plus, CheckCircle, Megaphone } from "@phosphor-icons/react";
import CampaignModal from "@/components/CampaignModal";

const scoreColor = (s) => (s >= 70 ? "text-emerald-700 bg-emerald-50 border-emerald-200" : s >= 45 ? "text-amber-700 bg-amber-50 border-amber-200" : "text-slate-600 bg-slate-50 border-slate-200");

export default function Prospection() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ departement: "", limit: 12, min_transmission: "" });
  const [job, setJob] = useState(null);
  const [launching, setLaunching] = useState(false);
  const [added, setAdded] = useState({});
  const [selected, setSelected] = useState([]);
  const [campaign, setCampaign] = useState(null);
  const [campaignLoading, setCampaignLoading] = useState(false);
  const pollRef = useRef(null);

  const toggleSel = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const launchCampaign = async () => {
    if (selected.length === 0) { toast.info("Sélectionne au moins une cible."); return; }
    setCampaignLoading(true);
    try {
      const { data } = await api.post("/pipeline/campaign", { company_ids: selected });
      if (!data.drafts || data.drafts.length === 0) { toast.error("Aucun brouillon généré"); return; }
      setCampaign(data.drafts);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur génération campagne"); }
    finally { setCampaignLoading(false); }
  };

  const poll = (jobId) => {
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/pipeline/jobs/${jobId}`);
        setJob(data);
        if (data.status === "done" || data.status === "error") {
          clearInterval(pollRef.current);
          if (data.status === "error") toast.error(data.error || "Erreur du pipeline");
          else toast.success(`${data.results.length} cible(s) prête(s) · ${data.counts.courtiers} courtiers trouvés`);
        }
      } catch (e) { console.debug("pipeline poll error", e); /* keep polling */ }
    }, 2500);
  };

  const launch = async () => {
    if (!form.departement.trim()) { toast.error("Indiquez un département (ex. 75)"); return; }
    setLaunching(true); setJob(null); setAdded({});
    try {
      const { data } = await api.post("/pipeline/build", {
        departement: form.departement.trim(), limit: Number(form.limit) || 12,
        min_transmission: form.min_transmission ? Number(form.min_transmission) : null,
      });
      setJob({ status: "running", step: "Démarrage…", counts: { candidates: 0, courtiers: 0, enriched: 0 }, results: [] });
      poll(data.job_id);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur lancement"); }
    finally { setLaunching(false); }
  };

  useEffect(() => () => clearInterval(pollRef.current), []);

  const addToPipeline = async (r) => {
    try {
      await api.post("/deals", { company_id: r.company_id });
      setAdded((a) => ({ ...a, [r.company_id]: true }));
      toast.success(`${r.nom} ajouté au pipeline M&A`);
    } catch (e) {
      if (e.response?.status === 400) { setAdded((a) => ({ ...a, [r.company_id]: true })); toast.info("Déjà dans le pipeline"); }
      else toast.error("Erreur ajout pipeline");
    }
  };

  const running = job && (job.status === "running" || job.status === "queued");
  const pct = job ? Math.min(100, job.counts.courtiers ? Math.round((job.counts.enriched / Math.min(job.counts.courtiers, form.limit)) * 100) : (job.counts.candidates ? 30 : 10)) : 0;

  return (
    <div>
      <header className="h-16 px-8 flex items-center gap-3 border-b border-border bg-white sticky top-0 z-10">
        <Crosshair size={20} className="text-primary" weight="bold" />
        <h1 className="font-head font-bold text-xl">Prospection ciblée</h1>
        <span className="overline text-muted-foreground hidden md:inline">Import · ORIAS · Scoring · Top cibles</span>
      </header>

      <div className="p-8 max-w-5xl">
        {/* Form */}
        <div className="bg-white border border-border p-5 mb-6">
          <p className="text-sm text-muted-foreground mb-4">
            Lance une chasse automatisée : l'app importe les cabinets d'un <strong>département</strong>, ne garde que les
            <strong> courtiers (vérifiés ORIAS)</strong>, calcule le <strong>signal de transmission</strong> et te propose les cibles à contacter en priorité.
          </p>
          <div className="grid md:grid-cols-4 gap-3 items-end">
            <div>
              <label className="text-xs font-semibold uppercase text-muted-foreground">Département</label>
              <input data-testid="prospection-dept" value={form.departement} onChange={(e) => setForm({ ...form, departement: e.target.value })}
                placeholder="ex. 75, 69, 13…" className="w-full mt-1 px-3 py-2.5 text-sm border border-border outline-none focus:border-primary" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase text-muted-foreground">Nombre de cibles</label>
              <input data-testid="prospection-limit" type="number" min={1} max={25} value={form.limit} onChange={(e) => setForm({ ...form, limit: e.target.value })}
                className="w-full mt-1 px-3 py-2.5 text-sm border border-border outline-none focus:border-primary" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase text-muted-foreground">Signal min. (option)</label>
              <input data-testid="prospection-min" type="number" min={0} max={100} value={form.min_transmission} onChange={(e) => setForm({ ...form, min_transmission: e.target.value })}
                placeholder="ex. 50" className="w-full mt-1 px-3 py-2.5 text-sm border border-border outline-none focus:border-primary" />
            </div>
            <button data-testid="prospection-launch" onClick={launch} disabled={launching || running}
              className="flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold bg-primary text-white hover:bg-blue-800 disabled:opacity-50">
              <Crosshair size={16} /> {running ? "En cours…" : "Lancer la chasse"}
            </button>
          </div>
        </div>

        {/* Progress */}
        {job && (
          <div className="bg-white border border-border p-5 mb-6" data-testid="prospection-progress">
            <div className="flex items-center gap-2 mb-3">
              {running ? <Spinner size={16} className="text-primary animate-spin" /> : <CheckCircle size={16} className="text-emerald-600" weight="fill" />}
              <span className="text-sm font-semibold">{job.step}</span>
            </div>
            <div className="h-2 bg-secondary overflow-hidden mb-3">
              <div className="h-full bg-primary transition-all" style={{ width: `${job.status === "done" ? 100 : pct}%` }} />
            </div>
            <div className="flex gap-6 text-xs text-muted-foreground">
              <span><strong className="text-foreground">{job.counts.candidates}</strong> candidats</span>
              <span className="flex items-center gap-1"><ShieldCheck size={13} className="text-primary" /><strong className="text-foreground">{job.counts.courtiers}</strong> courtiers ORIAS</span>
              <span><strong className="text-foreground">{job.counts.enriched}</strong> enrichis & scorés</span>
            </div>
          </div>
        )}

        {/* Results */}
        {job && job.results && job.results.length > 0 && (
          <div className="bg-white border border-border" data-testid="prospection-results">
            <div className="px-4 py-3 border-b border-border flex items-center gap-2">
              <TrendUp size={16} className="text-primary" />
              <span className="font-head font-bold text-sm">Top cibles — par signal de transmission</span>
              <span className="overline text-muted-foreground">{job.results.length} cabinet(s)</span>
              <button data-testid="launch-campaign-btn" onClick={launchCampaign} disabled={campaignLoading || selected.length === 0}
                className="ml-auto flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-primary text-white hover:bg-blue-800 disabled:opacity-40">
                <Megaphone size={14} /> {campaignLoading ? "Rédaction IA…" : `Lancer une campagne${selected.length ? ` (${selected.length})` : ""}`}
              </button>
            </div>
            <div className="divide-y divide-border">
              {job.results.map((r, i) => (
                <div key={r.company_id} className="flex items-center gap-4 px-4 py-3 hover:bg-secondary" data-testid={`prospection-target-${i}`}>
                  <input type="checkbox" data-testid={`prospection-select-${i}`} checked={selected.includes(r.company_id)} onChange={() => toggleSel(r.company_id)} className="accent-primary cursor-pointer" />
                  <div className="w-6 text-center font-head font-bold text-muted-foreground">{i + 1}</div>
                  <div className={`w-14 shrink-0 text-center border px-1.5 py-1 ${scoreColor(r.transmission_score)}`}>
                    <div className="font-bold text-base leading-none">{r.transmission_score}</div>
                    <div className="text-[9px] uppercase tracking-wide">{r.niveau}</div>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold truncate">{r.nom}</span>
                      <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-blue-700 bg-blue-50 border border-blue-200 px-1.5 py-0.5 rounded-full shrink-0">
                        <ShieldCheck size={10} /> {r.categories.join(" · ") || "Courtier"}
                      </span>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {r.ville || "—"} · Dirigeant {r.age_dirigeant ?? "—"} ans · CA {eur(r.ca)} · Comm. {eur(r.commissions)}
                    </div>
                  </div>
                  <button data-testid={`prospection-view-${i}`} onClick={() => navigate(`/cabinet/${r.company_id}`)}
                    className="flex items-center gap-1 text-xs text-primary border border-border px-2 py-1.5 hover:bg-accent shrink-0">
                    Fiche <ArrowRight size={12} />
                  </button>
                  {added[r.company_id] ? (
                    <span className="flex items-center gap-1 text-xs text-emerald-600 shrink-0"><CheckCircle size={13} weight="fill" /> Pipeline</span>
                  ) : (
                    <button data-testid={`prospection-add-${i}`} onClick={() => addToPipeline(r)}
                      className="flex items-center gap-1 text-xs font-semibold text-white bg-primary px-2 py-1.5 hover:bg-blue-800 shrink-0">
                      <Plus size={12} /> Pipeline
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {job && job.status === "done" && job.results.length === 0 && (
          <div className="bg-white border border-border p-8 text-center text-muted-foreground text-sm">
            Aucune cible ne correspond aux critères. Essaie un autre département ou baisse le signal minimum.
          </div>
        )}
      </div>

      {campaign && <CampaignModal drafts={campaign} onClose={() => setCampaign(null)} />}
    </div>
  );
}
